/**
 * Type-narrowing helpers for untyped template refs (useTemplateRef() without a type argument).
 * Use these instead of type assertions so types stay accurate and the code follows
 * "prefer type narrowing over assertions" (code-patterns.mdc).
 *
 * All helpers are SSR-safe: they return null / false when HTMLElement is not defined (e.g. Node during VitePress SSR).
 */

const hasHTMLElement = typeof HTMLElement !== 'undefined';

/**
 * Returns the value as an HTMLElement if it is one, otherwise null.
 * Use when a template ref is bound to an HTML element.
 */
export function toElement(value: unknown): HTMLElement | null {
  return hasHTMLElement && value instanceof HTMLElement ? value : null;
}

/**
 * Type guard: true when value is an object with an $el property that is an HTMLElement.
 * Use for template refs bound to Vue component instances (Field, Dropdown, etc.).
 */
export function isComponentWithEl(value: unknown): value is { $el: HTMLElement } {
  if (!hasHTMLElement || !value || typeof value !== 'object' || !('$el' in value)) return false;

  // Intentional cast: we've confirmed '$el' in value; TypeScript needs this to narrow to { $el: HTMLElement }.
  const el = (value as { $el: unknown }).$el;

  return el instanceof HTMLElement;
}

/**
 * Returns the component's $el if value is a component-with-$el, otherwise null.
 */
export function toComponentEl(value: unknown): HTMLElement | null {
  return isComponentWithEl(value) ? value.$el : null;
}

/**
 * Returns a focusDate-like function if value has calendarRef.focusDate, otherwise null.
 * Use for v-calendar DatePicker refs when calling focusDate. The returned function
 * invokes focusDate with the correct `this` (the calendar ref object) so component
 * methods work when called.
 */
export function getCalendarFocusDate(value: unknown): ((date: Date) => void) | null {
  if (!value || typeof value !== 'object' || !('calendarRef' in value)) return null;

  const calendar = (value as { calendarRef?: { focusDate?: (d: Date) => void } }).calendarRef;
  const focusDate = calendar?.focusDate;

  if (typeof focusDate !== 'function') return null;

  return (date: Date) => focusDate.call(calendar, date);
}
